import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_BASE } from '../../lib/api';
import { mergeDeep, cloneDeep } from '../../utils/i18n';
import { STUDENT_DOCUMENTS_COPY } from '../../i18n/documents';
import { confirmDialog } from '../../utils/dialogs';
import {
    buildDocumentTypeLabelMap,
    formatDocumentKindLabel,
    getDocumentStatusMeta
} from '../../utils/documentUtils';

export default function StudentDocumentsModal({ onClose, copy: copyOverrides, onDocumentsUpdated }) {
    const copy = useMemo(() => mergeDeep(cloneDeep(STUDENT_DOCUMENTS_COPY.default), copyOverrides || {}), [copyOverrides]);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageViewer, setShowImageViewer] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState({});
    const documentTypeLabels = useMemo(() => buildDocumentTypeLabelMap(), []);
    const resolveDocumentType = useCallback((kind) => formatDocumentKindLabel(kind, documentTypeLabels), [documentTypeLabels]);

    const buildDocumentUrl = (docId) => {
        const token = localStorage.getItem('go2cdl_access_token');
        const baseUrl = `${API_BASE}/api/documents/${docId}/download`;
        return token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
    };

    const fetchAllDocuments = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const token = localStorage.getItem('go2cdl_access_token');
            const response = await fetch(`${API_BASE}/api/documents/`, {
                credentials: 'include',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (response.ok) {
                const data = await response.json();
                setDocuments(data);
            } else {
                setError(copy.errors?.fetch || STUDENT_DOCUMENTS_COPY.default.errors.fetch);
            }
        } catch (error) {
            console.error("Error fetching documents:", error);
            setError(copy.errors?.fetch || STUDENT_DOCUMENTS_COPY.default.errors.fetch);
        } finally {
            setLoading(false);
        }
    }, [copy]);

    useEffect(() => {
        fetchAllDocuments();
    }, [fetchAllDocuments]);

    useEffect(() => {
        if (typeof onDocumentsUpdated === 'function') {
            onDocumentsUpdated(documents);
        }
    }, [documents, onDocumentsUpdated]);

    const handleImagePreview = async (docId, fileName) => {
        try {
            setError("");
            const token = localStorage.getItem('go2cdl_access_token');
            const response = await fetch(buildDocumentUrl(docId), {
                credentials: 'include',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!response.ok) {
                throw new Error(`Preview request failed with status ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setSelectedImage({ url, fileName });
            setShowImageViewer(true);
        } catch (error) {
            console.error("Error loading image:", error);
            setError(copy.errors?.loadImage || STUDENT_DOCUMENTS_COPY.default.errors.loadImage);
        }
    };

    const closeImageViewer = () => {
        if (selectedImage) {
            window.URL.revokeObjectURL(selectedImage.url);
        }
        setSelectedImage(null);
        setShowImageViewer(false);
    };

    const updateDocumentStatus = async (docId, status) => {
        const normalizedStatus = typeof status === 'string' ? status.toUpperCase() : '';
        if (!['PENDING', 'APPROVED', 'REJECTED'].includes(normalizedStatus)) {
            return;
        }

        try {
            setUpdatingStatus(prev => ({ ...prev, [docId]: normalizedStatus }));
            setError("");

            const response = await fetch(`${API_BASE}/api/documents/${docId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
                body: JSON.stringify({ status: normalizedStatus }),
            });

            if (!response.ok) {
                throw new Error(`Failed to update status: ${response.status}`);
            }

            const updatedDoc = await response.json();
            setDocuments(prev => prev.map(doc => (doc.id === updatedDoc.id ? updatedDoc : doc)));
        } catch (updateError) {
            console.error('Error updating document status:', updateError);
            setError(copy.errors?.updateStatus || 'Failed to update document status');
        } finally {
            setUpdatingStatus(prev => {
                const next = { ...prev };
                delete next[docId];
                return next;
            });
        }
    };

    const handleDelete = async (docId) => {
        if (!confirmDialog(copy.confirm?.delete || STUDENT_DOCUMENTS_COPY.default.confirm.delete)) {
            return;
        }

        try {
            const token = localStorage.getItem('go2cdl_access_token');
            const response = await fetch(`${API_BASE}/api/documents/${docId}`, {
                method: "DELETE",
                credentials: 'include',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (response.ok) {
                // Refresh the documents list
                fetchAllDocuments();
            } else {
                setError(copy.errors?.deleteFailed || STUDENT_DOCUMENTS_COPY.default.errors.deleteFailed);
            }
        } catch (error) {
            console.error("Error deleting document:", error);
            setError(copy.errors?.deleteFailed || STUDENT_DOCUMENTS_COPY.default.errors.deleteFailed);
        }
    };

    const handleDownload = async (docId, fileName) => {
        try {
            const token = localStorage.getItem('go2cdl_access_token');
            const response = await fetch(buildDocumentUrl(docId), {
                credentials: 'include',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (response.ok) {
                // Create a blob from the response
                const blob = await response.blob();

                // Create a download link
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();

                // Cleanup
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                setError(copy.errors?.downloadFailed || STUDENT_DOCUMENTS_COPY.default.errors.downloadFailed);
            }
        } catch (error) {
            console.error("Error downloading document:", error);
            setError(copy.errors?.downloadFailed || STUDENT_DOCUMENTS_COPY.default.errors.downloadFailed);
        }
    };

    const getDocumentIcon = (mime) => {
        if (mime.startsWith('image/')) return '🖼️';
        if (mime === 'application/pdf') return '📄';
        return '📋';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-3 sm:p-4 z-50 pt-16 sm:pt-20">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg sm:max-w-6xl max-h-[80vh] overflow-y-auto">
                    <div className="p-5 sm:p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">{copy.header?.title || STUDENT_DOCUMENTS_COPY.default.header.title}</h2>
                            </div>
                            <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
                        </div>

                        {error && (
                            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                                {error}
                            </div>
                        )}

                        {loading ? (
                            <div className="text-center py-8">
                                <div className="text-2xl mb-4">⏳</div>
                                <p className="text-slate-600">{copy.list?.loading || STUDENT_DOCUMENTS_COPY.default.list.loading}</p>
                            </div>
                        ) : documents.length === 0 ? (
                            <div className="text-center py-8">
                                <div className="text-4xl mb-4">📋</div>
                                <h3 className="text-lg font-medium text-slate-900 mb-2">{copy.list?.emptyTitle || STUDENT_DOCUMENTS_COPY.default.list.emptyTitle}</h3>
                                <p className="text-slate-600">{copy.list?.emptyMessage || STUDENT_DOCUMENTS_COPY.default.list.emptyMessage}</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {documents.map((doc) => {
                                    const statusMeta = getDocumentStatusMeta(doc.status);
                                    const normalizedStatus = (doc.status || 'PENDING').toUpperCase();
                                    const isUpdating = Boolean(updatingStatus[doc.id]);
                                    const updatingTarget = updatingStatus[doc.id];
                                    const reviewerLabel = doc.reviewerName || doc.reviewerEmail || '';

                                    return (
                                        <div
                                            key={doc.id}
                                            className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
                                        >
                                            <div className="flex items-start gap-3 sm:items-center">
                                                {doc.mime.startsWith('image/') ? (
                                                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                                                        <img
                                                            src={buildDocumentUrl(doc.id)}
                                                            alt={doc.title}
                                                            className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                                            onClick={() => handleImagePreview(doc.id, doc.title)}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'block';
                                                            }}
                                                        />
                                                        <div className="text-2xl" style={{ display: 'none' }}>{getDocumentIcon(doc.mime)}</div>
                                                    </div>
                                                ) : (
                                                    <div className="text-2xl">{getDocumentIcon(doc.mime)}</div>
                                                )}
                                                <div>
                                                    <h4 className="font-medium text-slate-900">{doc.title}</h4>
                                                    <div className="flex flex-wrap gap-2 text-sm text-slate-500">
                                                        <span>{resolveDocumentType(doc.kind)}</span>
                                                        <span>•</span>
                                                        <span>{formatFileSize(doc.size)}</span>
                                                        <span>•</span>
                                                        <span>{new Date(doc.uploadedAt).toLocaleDateString()}</span>
                                                        <span>•</span>
                                                        <span className="text-blue-600 font-medium">{doc.userName}</span>
                                                        <span>•</span>
                                                        <span className="text-gray-500 text-xs">{doc.userEmail}</span>
                                                        <span>•</span>
                                                        <span className="text-green-600 text-xs uppercase">{doc.userRole}</span>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-2 text-xs mt-2">
                                                        <span className={`px-2 py-1 font-semibold rounded-full ${statusMeta.badgeClass}`}>
                                                            {statusMeta.label}
                                                        </span>
                                                        {doc.reviewedAt && reviewerLabel && (
                                                            <span className="text-slate-400">
                                                                {reviewerLabel} • {new Date(doc.reviewedAt).toLocaleDateString()}
                                                            </span>
                                                        )}
                                                        {!doc.reviewedAt && normalizedStatus === 'PENDING' && (
                                                            <span className="text-slate-400">Awaiting review</span>
                                                        )}
                                                    </div>
                                                    {normalizedStatus === 'REJECTED' && (
                                                        <p className="text-xs text-red-600 mt-2">
                                                            Student needs to upload a replacement document.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex flex-col gap-3 w-full sm:w-auto">
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => updateDocumentStatus(doc.id, 'APPROVED')}
                                                        disabled={isUpdating || normalizedStatus === 'APPROVED'}
                                                        className={`px-3 py-2 rounded text-sm transition-colors ${normalizedStatus === 'APPROVED'
                                                            ? 'bg-green-200 text-green-800'
                                                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                                                            } ${isUpdating ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                    >
                                                        {updatingTarget === 'APPROVED' ? 'Saving...' : 'Approve'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => updateDocumentStatus(doc.id, 'REJECTED')}
                                                        disabled={isUpdating || normalizedStatus === 'REJECTED'}
                                                        className={`px-3 py-2 rounded text-sm transition-colors ${normalizedStatus === 'REJECTED'
                                                            ? 'bg-red-200 text-red-800'
                                                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                                                            } ${isUpdating ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                    >
                                                        {updatingTarget === 'REJECTED' ? 'Saving...' : 'Reject'}
                                                    </button>
                                                    {normalizedStatus !== 'PENDING' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => updateDocumentStatus(doc.id, 'PENDING')}
                                                            disabled={isUpdating}
                                                            className={`px-3 py-2 rounded text-sm transition-colors bg-yellow-100 text-yellow-700 hover:bg-yellow-200 ${isUpdating ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                        >
                                                            {updatingTarget === 'PENDING' ? 'Saving...' : 'Mark Pending'}
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="flex flex-wrap gap-2">
                                                    {doc.mime.startsWith('image/') && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleImagePreview(doc.id, doc.title)}
                                                            className="w-full sm:w-auto px-3 py-2 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200 transition-colors"
                                                        >
                                                            {copy.list?.previewButton || STUDENT_DOCUMENTS_COPY.default.list.previewButton}
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownload(doc.id, doc.title)}
                                                        className="w-full sm:w-auto px-3 py-2 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors"
                                                    >
                                                        {copy.list?.downloadButton || STUDENT_DOCUMENTS_COPY.default.list.downloadButton}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(doc.id)}
                                                        className="w-full sm:w-auto px-3 py-2 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition-colors"
                                                    >
                                                        {copy.list?.deleteButton || STUDENT_DOCUMENTS_COPY.default.list.deleteButton}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Full-Screen Image Viewer */}
                {showImageViewer && selectedImage && (
                    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]">
                        <div className="relative max-w-7xl max-h-full p-4">
                            {/* Close button */}
                            <button
                                onClick={closeImageViewer}
                                className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2"
                                aria-label={copy.header?.closeAria || STUDENT_DOCUMENTS_COPY.default.header.closeAria}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            {/* Image */}
                            <img
                                src={selectedImage.url}
                                alt={selectedImage.fileName}
                                className="max-w-full max-h-full object-contain rounded-lg"
                                style={{ maxHeight: '90vh' }}
                            />

                            {/* Image info */}
                            <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-3 rounded-lg">
                                <div className="text-sm font-medium">{selectedImage.fileName}</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
